import { formatNumber } from 'src/utils/formatNumber/formatNumber';
import { formatMetricValue } from 'src/utils/strategyHelpers';
import type { ProviderPopupMetric } from 'src/components/Maps/ProviderPopupDetails';

/**
 * Builds popup metric rows from a metric's display options and a provider's values for
 * it: one row per option that has a numeric value, formatted by its type. Location
 * options are skipped. An option without a label is shown by its code.
 *
 * @param displayOptions - the metric's display options
 * @param values - the provider's values for that metric, keyed by option code
 */
export const getMetricRows = (
  displayOptions: { code: string; label?: string; type?: string }[] | undefined,
  values: Record<string, unknown> | null | undefined
): ProviderPopupMetric[] =>
  (displayOptions ?? [])
    .filter(
      (option) =>
        option.type !== 'locations' && typeof values?.[option.code] === 'number'
    )
    .map((option) => ({
      key: option.code,
      label: option.label ?? option.code,
      value: formatMetricValue(
        values[option.code] as number,
        option.type,
        option.code
      ),
    }));

/**
 * Builds the popup metric rows for a map point. A point that brings its own
 * `metricRows` (an AI search's selected metric) uses them as they are; otherwise the
 * rows are its volume (labelled "Claims" or "Encounters" by `volumeLabel`) and
 * patients. A value the point doesn't carry gets no row.
 */
const getProviderPopupMetrics = (
  details:
    | {
        volume?: unknown;
        volumeLabel?: string;
        patients?: unknown;
        metricRows?: ProviderPopupMetric[];
      }
    | null
    | undefined
): ProviderPopupMetric[] => {
  if (Array.isArray(details?.metricRows)) return details.metricRows;

  const metrics: ProviderPopupMetric[] = [];
  if (typeof details?.volume === 'number') {
    metrics.push({
      key: 'volume',
      label: details.volumeLabel ?? 'Volume',
      value: formatNumber(details.volume),
    });
  }
  if (typeof details?.patients === 'number') {
    metrics.push({
      key: 'patients',
      label: 'Patients',
      value: formatNumber(details.patients),
    });
  }
  return metrics;
};

export default getProviderPopupMetrics;
